import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';
import * as styles from './LoanDetails.module.scss';
import ControlButton from '../ControlButton';
import PersonalizeIcon from './personalize.svg';
import Arrow from '@images/arrow.svg';
import Tooltip from '@components/Tooltip';
import useStyleVars from '@hooks/useStyleVars';
import useMatchMedia from '@hooks/useMatchMedia';
import useIsMobile from '@hooks/useIsMobile';
import useHasRendered from '@hooks/useHasRendered';
import { upperFirst } from 'lodash';
import { creditScoreRanges } from '@enums';
import classNames from 'classnames';
import {
  formatCurrency,
  isVowel,
  calculatePercent,
  getPropertyTypeText
} from '@helpers';

export default React.memo(function LoanDetails({
  state,
  inputsAreOpen,
  setInputsAreOpen,
  refreshData
}) {
  const text = useMemo(() => getLoanText(state), [state]);

  const hasRendered = useHasRendered();
  const [hideTooltip, setHideTooltip] = useState(inputsAreOpen);
  const tooltipTimer = useRef();

  const setTooltipTimer = useCallback(() => {
    clearTimeout(tooltipTimer.current);

    tooltipTimer.current = setTimeout(() => {
      setHideTooltip(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (inputsAreOpen) {
      clearTimeout(tooltipTimer.current);
      setHideTooltip(true);
    }
  }, [inputsAreOpen, setTooltipTimer]);

  const { controlsStackedWidth, controlsMobileWidth } = useStyleVars();
  const isMobile = useIsMobile();
  const { buttonText, tooltipText } = useMatchMedia(
    ['(max-width: 935px) and (min-width: 471px)', 'max-width: 470px'],
    [
      {
        buttonText: 'Personalize your loan',
        tooltipText: 'Edit loan details'
      },
      {
        buttonText: 'Edit loan details'
      }
    ],
    {
      buttonText: 'Personalize your loan details',
      tooltipText: 'Edit loan'
    }
  );

  const tooltipPosition = useMatchMedia(
    [
      `(max-width: ${controlsStackedWidth}px)`,
      `(min-width: ${controlsMobileWidth + 1}px)`
    ].join(' and '),
    'right',
    'bottom'
  );

  const button = (
    <ControlButton
      id="personalize-loan-details"
      aria-expanded={isMobile ? null : inputsAreOpen}
      aria-controls={isMobile ? 'calculator-inputs-modal' : 'calculator-inputs'}
      className={classNames(styles.button, {
        [styles.open]: inputsAreOpen
      })}
      onMouseLeave={() => {
        if (!inputsAreOpen) {
          setTooltipTimer();
        }
      }}
      onClick={() => {
        setInputsAreOpen(prevIsOpen => {
          const isOpen = !prevIsOpen;

          if (!isOpen) {
            setTimeout(refreshData, 0);
          }

          return isOpen;
        });
      }}
    >
      <span className={styles.icon}>
        <PersonalizeIcon className={styles.personalize} />
      </span>
      <span>
        {hasRendered ? (
          buttonText
        ) : (
          <>
            <span className={styles.desktopText}>
              Personalize your loan{' '}
              <span className={styles.detailsText}>details</span>
            </span>
            <span
              aria-hidden="true"
              className={styles.mobileText}
            >
              Edit loan details
            </span>
          </>
        )}
      </span>
      {!isMobile && (
        <span className={styles.arrowWrapper}>
          <Arrow className={classNames(styles.arrow, styles.down)} />
          <Arrow className={classNames(styles.arrow, styles.up)} />
        </span>
      )}
    </ControlButton>
  );

  return (
    <div className={styles.wrapper}>
      {!hasRendered ? (
        <>
          <div className={classNames(styles.text, state.loanPurpose)}>
            {text}
          </div>
          <MobileLoanSummary state={state} />
        </>
      ) : (
        <>
          {!isMobile ? (
            <div className={classNames(styles.text, state.loanPurpose)}>
              {text}
            </div>
          ) : (
            <MobileLoanSummary state={state} />
          )}
        </>
      )}
      {!hasRendered ? (
        button
      ) : (
        <Tooltip
          text={tooltipText}
          inputId="personalize-loan-details"
          delay={600}
          position={tooltipPosition}
          forceHidden={inputsAreOpen || hideTooltip || isMobile}
        >
          {button}
        </Tooltip>
      )}
    </div>
  );
});

function MobileLoanSummary({ state }) {
  const creditScore = Object.values(creditScoreRanges).find(s => {
    return s.value === state.creditScore;
  });

  const creditScoreText = creditScore?.label;
  const propertyText = getPropertyTypeText(state.propertyType);
  const homeValueLabel =
    state.loanPurpose === 'purchase' ? 'Purchase Price' : 'Home Value';

  return (
    <ul className={styles.summary}>
      <Item
        label="Loan Amount"
        value={formatCurrency(state.loanAmount)}
      />
      <Item
        label={homeValueLabel}
        value={formatCurrency(state.homeValue)}
      />
      <Item
        label="Zip Code"
        value={state.zipCode}
      />
      <Item
        label="Credit Score"
        value={creditScoreText}
      />
      <Item
        label="Property"
        value={upperFirst(propertyText)}
      />
      <Item
        label="Occupancy"
        value={upperFirst(state.occupancyType.toLowerCase())}
      />
    </ul>
  );
}

function Item({ value, label }) {
  return !value || !label ? null : (
    <li>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value}</span>
    </li>
  );
}

function getLoanText(
  { loanPurpose, loanAmount, homeValue, zipCode, creditScore, propertyType },
  zipCodes
) {
  const parts = [];
  const isPurchase = loanPurpose === 'purchase';

  // Loan type
  parts.push(isPurchase ? 'Purchasing a' : 'Refinancing a');

  // Loan amount
  // if (loanAmount !== undefined) {
  //   parts.push(formatCurrency(homeValue, false));
  // }

  // parts.push(isPurchase ? 'home' : 'loan');
  // const formattedHomeValue = formatCurrency(homeValue);

  const propertyText = getPropertyTypeText(propertyType);

  if (isPurchase) {
    parts.push(`${formatCurrency(homeValue)} ${propertyText}`);
  } else {
    parts.push(`${formatCurrency(loanAmount)} loan`);
    parts.push(`for a ${formatCurrency(homeValue)} ${propertyText}`);
  }

  // Location
  // TODO add functionality to get cities from zip codes
  // TODO replace default zip code with configurable value
  // if (zipCode !== undefined) {
  //   zipCodes = zipCodes ?? {};
  //   const [city] = zipCodes[zipCode] ?? ['Livermore'];
  //   parts.push(`in ${city}`);
  // }

  // Credit score
  if (creditScore !== undefined) {
    const score = Object.values(creditScoreRanges).find(s => {
      return s.value === creditScore;
    });

    if (score?.label) {
      const a = isVowel(score.label) ? 'an' : 'a';
      parts.push(`with ${a} ${score.label.toLowerCase()} credit score`);
    }
  }

  // Down payment
  loanAmount = parseInt(loanAmount);
  homeValue = parseInt(homeValue);

  if (isPurchase && !isNaN(loanAmount) && !isNaN(homeValue)) {
    if (homeValue > loanAmount) {
      const percent = calculatePercent(
        homeValue - loanAmount,
        homeValue,
        false
      );

      parts.push(`and ${percent} down`);
    }
  }

  const text = parts.join(' ').replace(/\s\s+/g, ' ').replace(' .', '.');

  return upperFirst(`${text}.`);
}
